/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */


/*
* C block for land cover and land use map selection
*/
import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getAllowedStatements } from '../block_compatibility_checks';
import { AreaService } from '@/services/area.service';
import { areaChange } from '../helper_functions';

const defaultOptions = [
    ['World Cover 10m (2020)', 'world_cover'],
    ['Copernicus Global', 'copernicus_cover'],
    ['CORINE Land Cover (Europe)', 'corine'],
    ['Croplands', 'croplands']]

const globalOnlyOptions = [
    ['World Cover 10m (2020)', 'world_cover'],
    ['Copernicus Global', 'copernicus_cover'],
]

const copernicusYears = [
    ['2015', '_2015'],
    ['2016', '_2016'],
    ['2017', '_2017'],
    ['2018', '_2018'],
    ['2019', '_2019']
]

const corineYears = [
    ['1989 to 1998', '_1990'],
    ['1999 to 2001', '_2000'],
    ['2005 to 2007', '_2006'],
    ['2011 to 2012', '_2012'],
    ['2017 to 2018', '_2018']
]

Blockly.Blocks['input_land_cover'] = {
    init: function () {
        let areas = AreaService.getAreas();
        this.appendDummyInput("title")
            .appendField(new Blockly.FieldLabelSerializable("Land Cover"), "land_cover_title")

        this.appendDummyInput('dataset_input')
            .appendField(new Blockly.FieldLabelSerializable("Land cover data: "), "dataset_text")
            .appendField(new Blockly.FieldDropdown(this.generateOptions, this.validateDataset), "dropdown")

        this.appendDummyInput('area_input')
            .appendField(new Blockly.FieldLabelSerializable("Area: "), "area_text")
            .appendField(new Blockly.FieldDropdown(areas), "area")
        this.appendStatementInput("do")
            .setCheck(getAllowedStatements('land_cover'))
        this.setInputsInline(false);
        this.setStyle('landcover');
        this.setTooltip(Blockly.Msg.LAND_COVER_BLOCK_TOOLTIP);
        this.setHelpUrl("");
        this.previous_area = 'polygon';
        this.setOnChange(() => {
            if (this.getFieldValue('area') !== this.previous_area) {
                if (this.getFieldValue('area') == 'upload_shp') {
                    this.updateAreaParams_('add');
                } else if (this.previous_area == 'upload_shp') {
                    this.updateAreaParams_('remove');
                }
                this.previous_area = this.getFieldValue('area');
            }
        });

        this.areaSubscription = AreaService.areaChange$.subscribe( () => {
            let result = areaChange(this, "area_input", "area");
            if(!result) {
                this.areaSubscription.unsubscribe();
            }
        });
    },

    updateAreaParams_: function (param) {
        if (param == 'add') {
            this.getInput('area_input').appendField(" Path: ", "path_text")
                .appendField(new Blockly.FieldTextInput("user/shp_name"), "path");
        } else {
            try {
                this.getInput('area_input').removeField("path_text");
                this.getInput('area_input').removeField("path");
            } catch (e) {
                console.log('Input does not exist');
            }
        }
    },

    generateOptions: function() {

        var sourceBlock = this.getSourceBlock();

        if(sourceBlock && sourceBlock.data) {
            try {
                var config = JSON.parse(sourceBlock.data);
                if(config.limitToGlobalDatasets === true) {
                    return globalOnlyOptions
                }
            } catch(e) {
                console.error("couldn't parse block config");
            } 
        } 

        return defaultOptions
    },

    validateDataset: function(newValue) {
        let thisBlock = this.getSourceBlock();
        let datasetValue = newValue;
        let comparisonInput = thisBlock.getInput('comparison_input');
        let comparisonMethod = thisBlock.getInput('comparison_method');

        thisBlock.setWarningText(null, "SAME_DATE_LANDCOVER")

        // remove aditional years dropdown
        if (thisBlock.getField("data")) {
            thisBlock.removeFromYearInputFromBlock(thisBlock)
        }

        if (comparisonInput) {
            thisBlock.removeComparisonInputFromBlock(thisBlock)
        }

        if (comparisonMethod) {
            thisBlock.removeComparisonMethodFromBlock(thisBlock)
        }
        // check whether corine or copernicus data and append years dropdown accordingly
        if (datasetValue.includes('copernicus') || datasetValue.includes('corine')) {
            if (datasetValue.includes('copernicus')) {
                thisBlock.addFromYearInputToBlock(thisBlock, copernicusYears)

                thisBlock.addComparisonInputToBlock(thisBlock, [['None', 'none'], ...copernicusYears])
            } else if (datasetValue.includes('corine')) {
                thisBlock.addFromYearInputToBlock(thisBlock, corineYears)

                thisBlock.addComparisonInputToBlock(thisBlock, [['None', 'none'], ...corineYears])
            }
        }

        return newValue;
    },

    validateDates: function(newValue) {
        let thisBlock = this.getSourceBlock();
        let comparisonYear;
        let fieldName = this.name;

        if (fieldName.includes('comparison')) {
            comparisonYear = thisBlock.getFieldValue('data');
        } else {
            comparisonYear = thisBlock.getFieldValue('comparison_data');
        }
        
        thisBlock.setWarningText(null, "SAME_DATE_LANDCOVER")
        if (comparisonYear) {
            if (comparisonYear === newValue) {
                thisBlock.setWarningText("You're comparing the same dates, please choose distinct dates for comparison.", "SAME_DATE_LANDCOVER")
            }
        }
        return newValue;
    },

    addFromYearInputToBlock: function(block, datesOption) {
        block.appendDummyInput('from_year_input')
            .appendField('From year: ')
            .appendField(new Blockly.FieldDropdown(datesOption, this.validateDates), "data")
            .setAlign(Blockly.ALIGN_RIGHT)

        block.moveInputBefore("from_year_input", "area_input")
    },

    removeFromYearInputFromBlock: function(block) {
        block.removeInput("from_year_input")
    },

    addComparisonInputToBlock: function(block, datesOption) {
        block.appendDummyInput('comparison_input')
            .appendField('Compare to year: ')
            .appendField(new Blockly.FieldDropdown(datesOption, this.validateDates), "comparison_data")
            .setAlign(Blockly.ALIGN_RIGHT)
        
        block.moveInputBefore("comparison_input", "area_input")
    },

    removeComparisonInputFromBlock: function(block) {
        block.removeInput('comparison_input')
    }
};